/// <amd-module name="VSS/Ext/ExternalContentUtil" />

import { getPageContext, IVssPageContext, TeamFoundationHostType } from "VSS/Platform/Context";
import { ContextIdentifier, GlobalizationContext, TeamContext, TimeZonesConfiguration } from "VSS/Platform/Contracts/Platform";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { IVssLocationService } from "VSS/Platform/Location";
import { IVssPageService } from "VSS/Platform/Page";
import { getCookie, setCookie } from "VSS/Platform/Util/Cookie";
import { combineUrlPaths } from "VSS/Platform/Util/Url";
const galleryCdnCookieName = "GALLERY-CDN";

/**
 * Individual pieces of the extension id
 */
export interface IExtensionIdentifier {
    /**
     * Full id of the extension {publisher}.{extension}
     */
    id: string;

    /**
     * Id of the publisher
     */
    publisherId: string;

    /**
     * Id of the extension (without the publisher prefix)
     */
    extensionId: string;
}

export interface IExtensionDescriptor extends IExtensionIdentifier {
    /**
     * Version of the extension
     */
    version: string;
}

/**
 * Detailed Page Context passed to extension iframes in the initial handshake
 */
interface IDetailPageContext {
    globalization?: GlobalizationContext;
    timeZonesConfiguration?: TimeZonesConfiguration;
    webContext?: {
        project?: ContextIdentifier;
        team?: TeamContext;
    };
}

/**
 * Context passed to extension iframes in the initial handshake, and used
 * to resolve templated URLs
 */
export interface IExternalPageContext {
  host: {
    id: string;
    name: string;
    type: TeamFoundationHostType;
    serviceVersion: string;
    isHosted?: boolean;
  };
  user: {
    descriptor: string;
    id: string;
    name: string;
    displayName: string;
    imageUrl: string;
  };
  pageContext?: IDetailPageContext; 
  extension: IExtensionDescriptor;
}

/**
 * Takes a full extension or contribution id and parses it into individual pieces
 *
 * @param contributionId Full id of a contribution or extension
 */
export function getExtensionIdentifier(contributionId: string): IExtensionIdentifier {
    const extensionIdDotIndex = contributionId.indexOf(".");
    const publisherId = contributionId.substr(0, extensionIdDotIndex);

    let extensionId = contributionId.substr(extensionIdDotIndex + 1);
    const contributionIdDotIndex = extensionId.indexOf(".");
    if (contributionIdDotIndex !== -1) {
        extensionId = extensionId.substr(0, contributionIdDotIndex);
    }

    return {
        id: `${publisherId}.${extensionId}`,
        publisherId,
        extensionId
    };
}

/**
 * Gets the context passed to extension iframes in the initial handshake, and used
 * to resolve templated URLs
 *
 * @param pageContext Page context
 */
export function getExternalPageContext(pageContext: IVssPageContext, contributionId: string): IExternalPageContext {
    const pageData = pageContext.getService<IVssPageService>("IVssPageService").getData()!; // Basic information about the service and the entities accessed
    const pageContextData = getPageContext(contributionId);
    const context = {
        host: {
            id: pageData.hostId,
            name: pageData.hostName,
            serviceVersion: pageData.serviceVersion,
            type: pageData.hostType,
            isHosted: pageContextData?.webAccessConfiguration.isHosted
        },
        user: {
            descriptor: pageData.user.descriptor,
            id: pageData.user.id,
            name: pageData.user.uniqueName,
            displayName: pageData.user.displayName,
            imageUrl: pageData.user.imageUrl
        },
        pageContext: {
            globalization: pageContextData?.globalization,
            timeZonesConfiguration: pageContextData?.timeZonesConfiguration,
            webContext: {
                project: pageContextData?.webContext?.project,
                team: pageContextData?.webContext?.team
            }
        },
        extension: getExtensionIdentifier(contributionId) as IExtensionDescriptor
    };

    const contributionProvider = pageContext.getService<IVssContributionService>("IVssContributionService").getContributionProvider(contributionId);
    if (contributionProvider) {
        context.extension.version = contributionProvider.version;
    }
    return context;
}

/**
 * Returns true if we should use the fallback URI for extensions (i.e. gallery CDN disabled)
 */
export function isExtensionFallbackEnabled() {
    return getCookie(galleryCdnCookieName) === "disabled";
}

/**
 * Set the cookie to use fallback URLs for extension loading (i.e. disable the gallery CDN)
 */
export function useExtensionFallback() {
    setCookie(galleryCdnCookieName, "disabled", "/", undefined, 3600); // Disable CDN usage for 1 hour
}

/**
 * Gets the base URL for the extension that the specified contribution is from
 *
 * @param pageContext Page context
 * @param contributionId Id of the content contribution
 */
export function getExtensionBaseUrl(pageContext: IVssPageContext, contributionId: string): string | undefined {
    let baseUrl: string | undefined;

    const contributionSvc = pageContext.getService<IVssContributionService>("IVssContributionService");
    const provider = contributionSvc.getContributionProvider(contributionId);
    if (provider) {
        baseUrl = provider.fallbackBaseUri && isExtensionFallbackEnabled() ? provider.fallbackBaseUri : provider.baseUri;
    }

    return baseUrl;
}

const galleryServiceInstanceId = "00000029-0000-8888-8000-000000000000";

/**
 * Gets the marektplace URL for a given extension
 *
 * @param pageContext Page context
 * @param extensionId Id of the extension
 */
export function getExtensionMarketplaceUrl(pageContext: IVssPageContext, extensionId: string): Promise<string> {
    return pageContext
        .getService<IVssLocationService>("IVssLocationService")
        .getServiceLocationAsync(galleryServiceInstanceId, TeamFoundationHostType.Deployment)
        .then(galleryUrl => {
            const pageData = pageContext.getService<IVssPageService>("IVssPageService").getData()!;
            if (pageData.isHosted) {
                return combineUrlPaths(galleryUrl, "items?itemName=" + extensionId);
            } else {
                return combineUrlPaths(galleryUrl, "_gallery/items?itemName=" + extensionId);
            }
        });
}

/**
 * External content-hosting related properties from a contribution
 */
export interface IContributionContentProps {
    /**
     * The URL for the external content iframe, pulled from the contribution's "uri" property, resolved from the
     * contribution provider's base URL.
     */
    url: string;

    /**
     * True if the content uses the VSS SDK and is interactive with the host frame. This is true unless the contribution
     * explicitly specifies an interactive: false property.
     */
    interactive: boolean;
}

/**
 * Resolves any mustache-template formatted URLs
 *
 * @param url The url to resolve
 * @param templateContext Optional context to be used in template URL resolution
 */
function resolveTemplatedUrl(pageContext: IVssPageContext, contributionId: string, url: string, templateContext: any): Promise<string> {
    if (url.indexOf("{{") < 0) {
        return Promise.resolve(url);
    } else {
        return pageContext
            .getService<IVssContributionService>("IVssContributionService")
            .getContributionAsync("ms.vss-web.mustache-content")
            .then(() => {
                const externalPageContext = getExternalPageContext(pageContext, contributionId);
                const context = {
                    account: { id: externalPageContext.host.id, name: externalPageContext.host.name },
                    ...externalPageContext,
                    ...templateContext
                };
                try {
                    url = (window as any).LWL.req("mustache").render(url, context);
                } catch (ex) {
                    // Invalid template - just return the raw property value
                }
                return url;
            });
    }
}

/**
 * Gets external content-hosting related properties from a contribution
 *
 * @param pageContext Page context
 * @param contributionId Id of the contribution that contains external content
 * @param templateContext Optional context to be used in template URL resolution
 * @param contributionUri Optional uri value to use if different from the contribution's uri property.
 */
export function getContributionContentProps(
    pageContext: IVssPageContext,
    contributionId: string,
    templateContext?: any,
    contributionUri?: string
): Promise<IContributionContentProps | undefined> {
    return new Promise((resolve, reject) => {
        const contributionSvc = pageContext.getService<IVssContributionService>("IVssContributionService");
        contributionSvc.getContributionAsync<{ uri: string; interactive?: boolean }>(contributionId).then(contribution => {
            if (contribution && (contributionUri || contribution.uri)) {
                resolveTemplatedUrl(pageContext, contributionId, contributionUri || contribution.uri, templateContext).then(url => {
                    const interactive = contribution.interactive !== false;

                    // Append to the provider base URL if the url is relative
                    if (!url.startsWith("http://") && !url.startsWith("https://")) {
                        const baseUrl = getExtensionBaseUrl(pageContext, contributionId);
                        if (baseUrl) {
                            resolveTemplatedUrl(pageContext, contributionId, baseUrl, templateContext).then(resolvedBaseUrl => {
                                url = combineUrlPaths(resolvedBaseUrl, url);
                                resolve({ url, interactive });
                            });
                            return;
                        }
                    }

                    resolve({ url, interactive });
                });
            } else {
                resolve(undefined);
            }
        });
    });
}
